/**
 * ProgressBar Component
 *
 * Interactive progress bar for the media player.
 * Features:
 * - Click to seek to position
 * - Hover to preview chapter at position
 * - Chapter markers
 * - Buffering indicator
 * - Keyboard navigation support
 */

import { useState, useRef } from "react";
import type { MediaPlayerInstance } from "@vidstack/react";
import { Badge } from "@/components/ui/badge";
import {
	getPlayerActions,
	playerSelectors,
	usePlayerStore,
} from "@/lib/player/player.store";
import { KEYBOARD_SEEK_STEP_SECONDS } from "./player.constants";

interface ProgressBarProps {
	playerRef: React.RefObject<MediaPlayerInstance | null>;
	currentTime: number;
	totalDuration: number;
	chapters: Array<{ text: string; startTime: number; endTime: number }>;
	vidstackWaiting: boolean;
	previewLimit?: number | null; // Preview limit in seconds (for non-authenticated users)
	onPreviewLimitReached?: () => void; // Callback when user tries to seek beyond preview
}
/**
 * ProgressBar Component
 *
 * Displays the playback progress with:
 * - Chapter markers
 * - Hover preview with chapter badges
 * - Buffering indicator
 * - Click-to-seek functionality
 * - Keyboard navigation
 */
export function ProgressBar({
	playerRef,
	currentTime,
	totalDuration,
	chapters,
	vidstackWaiting,
	previewLimit = null,
	onPreviewLimitReached,
}: ProgressBarProps) {
	const progressBarRef = useRef<HTMLDivElement | null>(null);
	const [hoverPosition, setHoverPosition] = useState<{
		x: number;
		percentage: number;
	} | null>(null);

	// Get chapter markers from store
	const chapterMarkers = playerSelectors.getChapterMarkers(
		usePlayerStore.getState(),
	);

	/**
	 * Find chapter at specific time position
	 */
	const getChapterAtTime = (time: number) => {
		return chapters.find(
			(chapter) => time >= chapter.startTime && time < chapter.endTime,
		);
	};

	/**
	 * Handle mouse movement over progress bar
	 * Shows chapter preview at hover position
	 */
	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!progressBarRef.current || totalDuration <= 0) return;

		const rect = progressBarRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const percentage = Math.min(1, Math.max(0, x / rect.width));

		setHoverPosition({ x, percentage });
	};

	/**
	 * Handle mouse leave progress bar
	 * Hides chapter preview
	 */
	const handleMouseLeave = () => {
		setHoverPosition(null);
	};

	/**
	 * Handle click on progress bar
	 * Seeks to clicked position
	 * Blocks seeking beyond preview limit for non-authenticated users
	 */
	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!progressBarRef.current || totalDuration <= 0) return;

		const rect = progressBarRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const percentage = Math.min(1, Math.max(0, x / rect.width));
		const newTime = percentage * totalDuration;

		// Check if trying to seek beyond preview limit
		if (
			previewLimit !== null &&
			previewLimit !== undefined &&
			newTime > previewLimit
		) {
			// Block the seek and trigger preview limit callback
			if (onPreviewLimitReached) {
				onPreviewLimitReached();
			}
			return;
		}

		getPlayerActions().seek(newTime);
	};

	/**
	 * Handle keyboard navigation
	 * Arrow keys, Home, End for seeking
	 */
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!playerRef.current) return;

		const step = KEYBOARD_SEEK_STEP_SECONDS;

		if (e.key === "ArrowLeft") {
			playerRef.current.currentTime = Math.max(0, currentTime - step);
		} else if (e.key === "ArrowRight") {
			playerRef.current.currentTime = Math.min(
				totalDuration || currentTime,
				currentTime + step,
			);
		} else if (e.key === "Home") {
			playerRef.current.currentTime = 0;
		} else if (e.key === "End" && Number.isFinite(totalDuration)) {
			playerRef.current.currentTime = totalDuration;
		}
	};

	// Get chapter at hover position
	const hoverChapter =
		hoverPosition && totalDuration > 0
			? getChapterAtTime(hoverPosition.percentage * totalDuration)
			: null;

	const progressPercentage = playerSelectors.getProgressPercentage(
		usePlayerStore.getState(),
	);

	return (
		<div className="pointer-events-auto relative">
			<div
				ref={progressBarRef}
				className="group relative h-2 cursor-pointer bg-gray-900/50 hover:h-3 transition-all rounded-full overflow-hidden"
				onClick={handleClick}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onKeyDown={handleKeyDown}
				role="slider"
				tabIndex={0}
				aria-label="Seek"
				aria-valuemin={0}
				aria-valuemax={
					Number.isFinite(totalDuration) ? Math.floor(totalDuration) : 0
				}
				aria-valuenow={
					Number.isFinite(currentTime) ? Math.floor(currentTime) : 0
				}
			>
				{/* Chapter markers */}
				{chapterMarkers.map(
					(marker, idx) =>
						idx > 0 && (
							<div
								key={marker.chapter.text}
								className="absolute w-[3px] top-0 bottom-0 bg-gray-700 pointer-events-none"
								style={{ left: `${marker.position}%` }}
								aria-hidden
							/>
						),
				)}

				{/* Progress fill */}
				<div
					className="h-full bg-gradient-to-r from-primary via-primary to-primary/80 pointer-events-none rounded-full"
					style={{ width: `${progressPercentage}%` }}
					aria-hidden
				/>

				{/* Buffering indicator with shimmer animation */}
				{vidstackWaiting && (
					<div
						className="absolute inset-0 overflow-hidden pointer-events-none rounded-full"
						aria-hidden
					>
						<div
							className="h-full w-[200%] bg-gradient-to-r from-transparent via-gray-400/50 to-transparent"
							style={{
								animation: "shimmer 1.5s ease-in-out infinite",
								transform: "translateX(-50%)",
							}}
						/>
					</div>
				)}

				{/* Hover indicator (white line at current position) */}
				<div
					className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
					aria-hidden
				>
					<div
						className="absolute top-0 h-full w-1 bg-white rounded-full shadow-lg shadow-white/50"
						style={{ left: `${progressPercentage}%` }}
					/>
				</div>
			</div>

			{/* Chapter badge on hover - positioned relative to outer container */}
			{hoverChapter && hoverPosition && (
				<div
					className="absolute -top-12 transform -translate-x-1/2 pointer-events-none animate-in fade-in zoom-in-95 duration-200 z-10"
					style={{ left: `${hoverPosition.percentage * 100}%` }}
				>
					<div className="relative">
						<Badge
							variant="default"
							className="relative bg-gradient-to-br from-white/10 via-white/5 to-white/5 text-white shadow-[0_4px_12px_0_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.12)] backdrop-blur-lg border border-white/20 px-4 py-2 text-xs font-semibold whitespace-nowrap overflow-hidden"
						>
							<span className="relative z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
								{hoverChapter.text}
							</span>
							<div className="absolute inset-0 rounded-md bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-30" />
						</Badge>
						{/* Arrow pointing down */}
						<div className="absolute left-1/2 -bottom-1 transform -translate-x-1/2 z-0">
							<div className="w-2.5 h-2.5 bg-gradient-to-br from-white/10 via-white/5 to-white/5 rotate-45 border-r border-b border-white/20 shadow-[0_2px_8px_0_rgba(0,0,0,0.25)] backdrop-blur-lg" />
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
